import { useState } from "react";
import { useController } from "react-hook-form";

import { cn } from "@/lib/utils";
import {
  ImageUploader,
  ImageUploaderResultTypes,
} from "molecules/ImageUploader";

import { useFieldIds } from "../../useFieldIds";
import FieldError from "../FieldError/FieldError";
import FieldHint from "../FieldHint/FieldHint";
import FieldLabel from "../FieldLabel/FieldLabel";

type ImageUploaderFieldProps = {
  readonly name: string;
  readonly label?: string;
  readonly hint?: string;
  readonly disabled?: boolean;
  readonly imageHeight?: number;
  readonly imageWidth?: number;
  readonly resolutionHeight?: number;
  readonly resolutionWidth?: number;
  readonly multiple?: boolean;
};

const MAX_IMAGES = 5;

export default function ImageUploaderField({
  name,
  label,
  hint,
  disabled = false,
  imageHeight = 250,
  imageWidth = 252,
  resolutionHeight = 804,
  resolutionWidth = 1074,
  multiple = false,
}: ImageUploaderFieldProps): React.ReactElement {
  const { inputId, labelId, hintId, errorId } = useFieldIds(name);

  const { field, fieldState } = useController({
    name,
  });

  const [newImageKey, setNewImageKey] = useState(0);
  const invalid = !!fieldState.error;

  const imageUrls = field.value || [];

  const handleNewImageChange = (value: string | { resourceUrl: string }) => {
    const url = typeof value === "string" ? value : value.resourceUrl;
    if (imageUrls.length < MAX_IMAGES && !imageUrls.includes(url)) {
      field.onChange([...imageUrls, url]);
      setNewImageKey((prev) => prev + 1);
    }
  };

  const handleImageChange = (
    value: string | { resourceUrl: string },
    idx: number
  ) => {
    const url = typeof value === "string" ? value : value.resourceUrl;
    const updatedUrls = [...imageUrls];
    updatedUrls.splice(idx, 1, url);
    field.onChange(updatedUrls);
  };

  const handleRemoveImage = (idx: number) => {
    const updatedUrls = [...imageUrls];
    updatedUrls.splice(idx, 1);
    field.onChange(updatedUrls);
  };

  const canAddMoreImages = imageUrls.length < MAX_IMAGES;

  return (
    <div
      className={cn("ImageUploaderField", "grid gap-2")}
      data-testid="ImageUploaderField"
    >
      {label && (
        <FieldLabel
          htmlFor={inputId}
          id={labelId}
          invalid={invalid}
          value={label}
        />
      )}

      {multiple ? (
        <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
          {imageUrls.map((imageUrl: string, idx: number) => (
            <div className="flex-shrink-0" key={imageUrl + idx}>
              <ImageUploader
                disabled={disabled}
                imageHeight={imageHeight}
                imageWidth={imageWidth}
                name={`${name}-${idx}`}
                resolutionHeight={resolutionHeight}
                resolutionWidth={resolutionWidth}
                value={imageUrl}
                onChange={(
                  value: string | ImageUploaderResultTypes<"/uploads">
                ) => handleImageChange(value, idx)}
                onDelete={() => handleRemoveImage(idx)}
              />
            </div>
          ))}

          {canAddMoreImages && (
            <div className="flex-shrink-0" key={`new-image-${newImageKey}`}>
              <ImageUploader
                disabled={disabled}
                imageHeight={imageHeight}
                imageWidth={imageWidth}
                invalid={invalid}
                resolutionHeight={resolutionHeight}
                resolutionWidth={resolutionWidth}
                onChange={(
                  value: string | ImageUploaderResultTypes<"/uploads">
                ) => handleNewImageChange(value)}
              />
            </div>
          )}
        </div>
      ) : (
        <ImageUploader
          disabled={disabled}
          imageHeight={imageHeight}
          imageWidth={imageWidth}
          invalid={invalid}
          resolutionHeight={resolutionHeight}
          resolutionWidth={resolutionWidth}
          value={field.value || ""}
          onChange={(value: string | ImageUploaderResultTypes<"/uploads">) => {
            const url = typeof value === "string" ? value : value.resourceUrl;
            field.onChange(url);
          }}
          onDelete={() => field.onChange("")}
        />
      )}

      <div className={cn("align-center flex h-4 justify-end")}>
        {hint && !fieldState.error && <FieldHint hint={hint} id={hintId} />}
        {fieldState.error && (
          <FieldError error={fieldState.error} id={errorId} />
        )}
      </div>
    </div>
  );
}
